import * as React from "react"
import type {
  DefaultLegendContentProps,
  DefaultTooltipContentProps,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipValueType,
} from "recharts"
import { Legend as RechartsLegend, ResponsiveContainer as RechartsResponsiveContainer, Tooltip as RechartsTooltip } from "recharts"

import { cn } from "../../lib/utils"

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const

const INITIAL_DIMENSION = { width: 320, height: 200 } as const
type TooltipNameType = number | string

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
>

type ChartContextProps = {
  config: ChartConfig
}

type ResponsiveContainerProps = React.ComponentProps<typeof ResponsiveContainer>
type TooltipProps = React.ComponentProps<typeof Tooltip>
type LegendProps = React.ComponentProps<typeof Legend>

const ChartContext = React.createContext<ChartContextProps | null>(null)

const ChartTooltip = RechartsTooltip
const ChartLegend = RechartsLegend

function useChart() {
  const context = React.use(ChartContext)

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }

  return context
}

function ChartContainer({
  id,
  className,
  style,
  children,
  config,
  initialDimension = INITIAL_DIMENSION,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig
  children: ResponsiveContainerProps["children"]
  initialDimension?: {
    width: number
    height: number
  }
}) {
  const uniqueId = React.useId()
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`
  const contextValue = React.useMemo(() => ({ config }), [config])

  return (
    <ChartContext.Provider value={contextValue}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "[position:relative] [min-height:var(--chart-initial-height)] [width:100%] [display:flex] [justify-content:center] [font-size:0.75rem] [line-height:1rem] [&_.recharts-cartesian-axis-tick_text]:[fill:var(--muted-foreground)] [&_.recharts-cartesian-grid_line[stroke='#ccc']]:[stroke:color-mix(in_srgb,_var(--border)_50%,_transparent)] [&_.recharts-curve.recharts-tooltip-cursor]:[stroke:var(--border)] [&_.recharts-dot[stroke='#fff']]:[stroke:transparent] [&_.recharts-polar-grid_[stroke='#ccc']]:[stroke:var(--border)] [&_.recharts-radial-bar-background-sector]:[fill:var(--muted)] [&_.recharts-rectangle.recharts-tooltip-cursor]:[fill:var(--muted)] [&_.recharts-reference-line_[stroke='#ccc']]:[stroke:var(--border)] [&_.recharts-sector[stroke='#fff']]:[stroke:transparent]",
          className
        )}
        {...props}
        style={{ ...style, "--chart-initial-height": `${initialDimension.height}px` } as React.CSSProperties}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={initialDimension}
          style={{ minHeight: `${initialDimension.height}px` } as React.CSSProperties}
        >
          {children}
        </RechartsResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme ?? config.color
  )

  if (!colorConfig.length) {
    return null
  }

  const css = Object.entries(THEMES)
    .map(
      ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ??
      itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join("\n")}
}
`
    )
    .join("\n")

  return (
    <style>{css}</style>
  )
}

function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: TooltipProps &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean
    hideIndicator?: boolean
    indicator?: "line" | "dot" | "dashed"
    nameKey?: string
    labelKey?: string
  } & Omit<DefaultTooltipContentProps<TooltipValueType, TooltipNameType>, "accessibilityLayer">) {
  const { config } = useChart()

  const tooltipLabel = (() => {
    if (hideLabel || !payload?.length) {
      return null
    }

    const [item] = payload
    const key = `${labelKey ?? item?.dataKey ?? item?.name ?? "value"}`
    const itemConfig = getPayloadConfigFromPayload(config, item, key)
    const value =
      !labelKey && typeof label === "string"
        ? (config[label]?.label ?? label)
        : itemConfig?.label

    if (labelFormatter) {
      return (
        <div className={cn("[font-weight:500]", labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      )
    }

    if (!value) {
      return null
    }

    return <div className={cn("[font-weight:500]", labelClassName)}>{value}</div>
  })()

  if (!active || !payload?.length) {
    return null
  }

  const nestLabel = payload.length === 1 && indicator !== "dot"
  const tooltipItems: React.ReactNode[] = []
  for (const [index, item] of payload.entries()) {
    if (item.type === "none") {
      continue
    }

    const key = `${nameKey ?? item.name ?? item.dataKey ?? "value"}`
    const itemConfig = getPayloadConfigFromPayload(config, item, key)
    const indicatorColor = color ?? item.payload?.fill ?? item.color

    tooltipItems.push(
      <div
        key={`${item.dataKey ?? key}`}
        className={cn(
          "[display:flex] [width:100%] [flex-wrap:wrap] [align-items:stretch] [gap:0.5rem] [&>svg]:[color:var(--muted-foreground)]",
          indicator === "dot" && "[align-items:center]"
        )}
      >
        {formatter && item?.value !== undefined && item.name ? (
          formatter(item.value, item.name, item, index, item.payload)
        ) : (
          <>
            {itemConfig?.icon ? (
              <itemConfig.icon />
            ) : (
              !hideIndicator && (
                <div
                  className={cn(
                    "[flex-shrink:0] [border-radius:0.25rem]",
                    {
                      "h-2.5 w-2.5": indicator === "dot",
                      "w-1": indicator === "line",
                      "w-0 border border-dashed bg-transparent": indicator === "dashed",
                      "my-0.5": nestLabel && indicator === "dashed",
                    }
                  )}
                  style={
                    {
                      "--color-bg": indicatorColor,
                      "--color-border": indicatorColor,
                    } as React.CSSProperties
                  }
                />
              )
            )}
            <div
              className={cn(
                "[display:flex] [flex:1_1_0%] [justify-content:space-between] [line-height:1]",
                nestLabel ? "[align-items:flex-end]" : "[align-items:center]"
              )}
            >
              <div className={"[display:grid] [gap:0.375rem]"}>
                {nestLabel ? tooltipLabel : null}
                <span className={"[color:var(--muted-foreground)]"}>
                  {itemConfig?.label ?? item.name}
                </span>
              </div>
              {item.value != null && (
                <span className={"[font-family:var(--fonts-mono)] [font-weight:500] [color:var(--foreground)] [font-variant-numeric:tabular-nums]"}>
                  {typeof item.value === "number"
                    ? item.value.toLocaleString()
                    : String(item.value)}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "[display:grid] [min-width:8rem] [align-items:flex-start] [gap:0.375rem] [border-radius:0.5rem] [border-width:1px] [border-style:solid] [border-color:var(--color-border-primary)] [background-color:var(--color-bg-elevated)] [box-shadow:var(--shadow-L2)] [padding-inline:0.625rem] [padding-block:0.375rem] [font-size:0.75rem] [line-height:1rem]",
        className
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className={"[display:grid] [gap:0.375rem]"}>{tooltipItems}</div>
    </div>
  )
}

function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey,
}: React.ComponentProps<"div"> & {
  hideIcon?: boolean
  nameKey?: string
} & DefaultLegendContentProps) {
  const { config } = useChart()

  if (!payload?.length) {
    return null
  }

  const legendItems: React.ReactNode[] = []
  for (const item of payload) {
    if (item.type === "none") {
      continue
    }

    const key = `${nameKey ?? item.dataKey ?? "value"}`
    const itemConfig = getPayloadConfigFromPayload(config, item, key)

    legendItems.push(
      <div
        key={`${item.dataKey ?? key}`}
        className={cn(
          "[display:flex] [align-items:center] [gap:0.375rem] [&>svg]:[color:var(--muted-foreground)]"
        )}
      >
        {itemConfig?.icon && !hideIcon ? (
          <itemConfig.icon />
        ) : (
          <div
            className={"[width:0.5rem] [height:0.5rem] [flex-shrink:0] [border-radius:0.25rem]"}
            style={{
              backgroundColor: item.color,
            }}
          />
        )}
        {itemConfig?.label}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "[display:flex] [align-items:center] [justify-content:center] [gap:1rem]",
        verticalAlign === "top" ? "[padding-bottom:0.75rem]" : "[padding-top:0.75rem]",
        className
      )}
    >
      {legendItems}
    </div>
  )
}

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined
  }

  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined

  let configLabelKey: string = key

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string
  }

  return configLabelKey in config ? config[configLabelKey] : config[key]
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
}
